/**
 * Aleatoire deterministe et bruit coherent.
 *
 * Tout ce qui genere du monde doit passer par ici: meme graine, meme monde,
 * sur le client comme sur le serveur. Aucune dependance, aucun Math.random.
 */

/** Melangeur d'entiers 32 bits (mulberry32). */
export function creerAlea(graine: number): () => number {
  let a = graine >>> 0
  return function () {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Generateur pratique avec des aides typees. */
export class Alea {
  private suivant: () => number

  constructor(graine: number) {
    this.suivant = creerAlea(graine)
  }

  /** Flottant dans [0, 1). */
  reel(): number {
    return this.suivant()
  }

  /** Flottant dans [min, max). */
  entre(min: number, max: number): number {
    return min + this.suivant() * (max - min)
  }

  /** Entier dans [min, max]. */
  entier(min: number, max: number): number {
    return Math.floor(min + this.suivant() * (max - min + 1))
  }

  /** Vrai avec la probabilite p. */
  chance(p: number): boolean {
    return this.suivant() < p
  }

  /** Un element au hasard. Le tableau ne doit pas etre vide. */
  choix<T>(liste: readonly T[]): T {
    return liste[Math.floor(this.suivant() * liste.length)] as T
  }

  /** Melange sur place (Fisher-Yates). */
  melanger<T>(liste: T[]): T[] {
    for (let i = liste.length - 1; i > 0; i--) {
      const j = Math.floor(this.suivant() * (i + 1))
      const tmp = liste[i] as T
      liste[i] = liste[j] as T
      liste[j] = tmp
    }
    return liste
  }
}

// ------------------------------------------------------------------ bruit

/** Hachage 2D deterministe renvoyant [0, 1). */
export function hache2(x: number, z: number, graine: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(z | 0, 668265263) ^ Math.imul(graine | 0, 2246822519)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

/** Interpolation lissee (smootherstep). */
function lisse(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10)
}

/**
 * Bruit de valeur 2D dans [0, 1), continu et derivable.
 * L'echelle est en unites de grille: bruitValeur(x/40, z/40, s) donne des
 * motifs d'environ 40 metres.
 */
export function bruitValeur(x: number, z: number, graine: number): number {
  const x0 = Math.floor(x)
  const z0 = Math.floor(z)
  const fx = lisse(x - x0)
  const fz = lisse(z - z0)

  const a = hache2(x0, z0, graine)
  const b = hache2(x0 + 1, z0, graine)
  const c = hache2(x0, z0 + 1, graine)
  const d = hache2(x0 + 1, z0 + 1, graine)

  const haut = a + (b - a) * fx
  const bas = c + (d - c) * fx
  return haut + (bas - haut) * fz
}

/**
 * Bruit fractal: plusieurs octaves de bruit de valeur.
 * Renvoie une valeur dans [0, 1).
 */
export function fbm(x: number, z: number, graine: number, octaves = 4, lacunarite = 2, gain = 0.5): number {
  let somme = 0
  let amplitude = 1
  let frequence = 1
  let total = 0
  for (let i = 0; i < octaves; i++) {
    somme += bruitValeur(x * frequence, z * frequence, graine + i * 7919) * amplitude
    total += amplitude
    amplitude *= gain
    frequence *= lacunarite
  }
  return somme / total
}

/**
 * Bruit fractal "crete" (ridged), utile pour les montagnes.
 * Renvoie une valeur dans [0, 1).
 */
export function fbmCrete(x: number, z: number, graine: number, octaves = 4): number {
  let somme = 0
  let amplitude = 1
  let frequence = 1
  let total = 0
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(bruitValeur(x * frequence, z * frequence, graine + i * 6151) * 2 - 1)
    somme += n * n * amplitude
    total += amplitude
    amplitude *= 0.5
    frequence *= 2
  }
  return somme / total
}

// ------------------------------------------------------------------ aides

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v
}

/** Interpolation lineaire. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Rampe lisse entre deux bornes, comme smoothstep de GLSL. */
export function smoothstep(bord0: number, bord1: number, x: number): number {
  const t = clamp((x - bord0) / (bord1 - bord0 || 1e-6), 0, 1)
  return t * t * (3 - 2 * t)
}

/** Melange deux couleurs 0xRRGGBB. */
export function melangerCouleur(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255
  const ag = (a >> 8) & 255
  const ab = a & 255
  const br = (b >> 16) & 255
  const bg = (b >> 8) & 255
  const bb = b & 255
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bl = Math.round(ab + (bb - ab) * t)
  return (r << 16) | (g << 8) | bl
}

/** Distance au carre entre deux points du plan. */
export function dist2(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx
  const dz = az - bz
  return dx * dx + dz * dz
}

/** Distance entre deux points du plan. */
export function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.sqrt(dist2(ax, az, bx, bz))
}
